// src/app/sidebar.component.ts
import { 
  Component, 
  Input, 
  HostBinding, 
  SimpleChanges, 
  Output, 
  EventEmitter,
  OnDestroy,
  inject,
  OnChanges
} from '@angular/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { KeycloakAuthService } from '../services/keycloak-auth.service';
import { PermisionService } from '../services/permision.service';
import { Router } from '@angular/router'; 

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    MatSidenavModule,
    MatListModule,
    MatDividerModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    RouterModule,
    CommonModule
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnChanges, OnDestroy {
  private authService = inject(KeycloakAuthService);
  private permissionService = inject(PermisionService);
  private router = inject(Router);

  @Input() userName: string | null = null;
  @Input() userSurname: string | null = null;
  @Input() isExpanded: boolean = true;
  @Output() toggle = new EventEmitter<void>();

  @HostBinding('class.collapsed')
  get collapsedClass(): boolean {
    return !this.isExpanded;
  }

  userInitials: string = '??';
  accessibleNavItems: any[] = [];

  public navItems = [
    { section: 'bots', link: '/course', icon: 'smart_toy', text: 'Bots' },
    { section: 'learning-management', link: '/learning-management', icon: 'description', text: 'Learning Management' },
    { section: 'courses', link: '/courses', icon: 'school', text: 'Courses' },
    { section: 'topics', link: '/topics', icon: 'topic', text: 'Topics' },
    { section: 'cohorts', link: '/cohorts', icon: 'groups', text: 'Cohorts' },
    { section: 'quizzes', link: '/quizzes', icon: 'quiz', text: 'Quizzes', dividerAfter: true },
    { section: 'content-management', link: '/content-management', icon: 'folder_managed', text: 'Content Management' },
    { section: 'property-management', link: '/property-management', icon: 'domain', text: 'Property management' },
    { section: 'student-management', link: '/student-management', icon: 'manage_accounts', text: 'Student management' },
    { section: 'reports', link: '/report', icon: 'analytics', text: 'Reports and Analytics', dividerAfter: true },
    { section: 'user-management', link: '/user-management', icon: 'admin_panel_settings', text: 'User Management' },
    { section: 'integrations', link: '/integrations', icon: 'integration_instructions', text: 'Integration and API' },
    { section: 'security', link: '/security', icon: 'security', text: 'Security Settings' },
  ];

  constructor() {
    this.updateAccessibleNavItems();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userName'] || changes['userSurname']) {
      this.userInitials = this.calculateInitials(this.userName, this.userSurname);
    }
    this.updateAccessibleNavItems();
  }

  ngOnDestroy(): void {}

  onToggleClick(): void {
    this.isExpanded = !this.isExpanded;
    this.toggle.emit();
  }

  onResetPassword(): void {
    // TODO: implement
  }

  onLogout(): void {
    this.authService.logout();
  }

  hasAccess(section: string): boolean {
    return this.permissionService.hasAccess(section);
  }

  private updateAccessibleNavItems(): void {
    this.accessibleNavItems = this.navItems.filter(item => this.hasAccess(item.section));
  }

  private calculateInitials(firstName: string | null, lastName: string | null): string {
    const first = firstName ? firstName[0] : '';
    const last = lastName ? lastName[0] : '';
    return first && last ? `${first}${last}`.toUpperCase() : first || last || '??';
  }

  // NEW: Helper to determine if a route is active
  isActive(link: string): boolean {
    return this.router.url === link;
  }
}